import WeightInfo, { TotalWeight } from '../../components/WeightInfo';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { get, post } from '../../lib/api';
import { connectSocket } from '../../lib/socket';
import { Button } from '../../components/ui/button';
import { orderReference } from '../../lib/orderReference';
import { ArrowLeft, Star } from 'lucide-react';

const statusLabel = {
  PENDING_PAYMENT: 'Pending payment',
  PENDING: 'Order is placed',
  PAID: 'Order is placed',
  PREPARING: 'Preparing',
  READY: 'Waiting for rider',
  OUT_FOR_DELIVERY: 'Out for delivery',
  FAILED: 'Payment failed',
  REFUNDED: 'Refunded',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

const paymentLabel = {
  COD: 'Cash on Delivery',
  COP: 'Cash on Pickup',
  GCASH: 'GCash',
  MAYA: 'Maya',
  CARD: 'Card',
  BANK_TRANSFER: 'Bank Transfer',
};

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellationBusy, setCancellationBusy] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState('');
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingBusy, setRatingBusy] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [ratingMessage, setRatingMessage] = useState('');

  useEffect(() => {
    let active = true;
    let requestVersion = 0;
    let initial = true;
    const fetchOrder = async () => {
      const version = ++requestVersion;
      try {
        if (initial) setLoading(true);
        initial = false;
        const response = await get(`/orders/${id}`);
        if (active && version === requestVersion && response.success) {
          setError('');
          setOrder(response.order);
          setDelivery(response.delivery);
        } else if (active && version === requestVersion) {
          setError(response.message || 'Unable to load order details.');
        }
      } catch (err) {
        if (active && version === requestVersion) {
          console.error('Failed to load order details:', err);
          setError(err.response?.data?.message || 'Unable to load order details.');
        }
      } finally {
        if (active && version === requestVersion) setLoading(false);
      }
    };

    fetchOrder();
    const socket = connectSocket();
    const refreshForOrder = (payload) => {
      const changedOrderId = payload?.orderId?._id || payload?.orderId || payload?._id;
      if (!changedOrderId || String(changedOrderId) === String(id)) fetchOrder();
    };
    socket.on('order:update', refreshForOrder);
    socket.on('delivery:updated', refreshForOrder);
    const timer = setInterval(fetchOrder, 30000);
    return () => {
      active = false;
      clearInterval(timer);
      socket.off('order:update', refreshForOrder);
      socket.off('delivery:updated', refreshForOrder);
    };
  }, [id]);

  const renderTimeline = () => {
    const orderStatus = order?.status;
    const deliveryStatus = delivery?.status;
    const isDelivery = order?.orderType === 'DELIVERY';
    const cashPayment = ['COD', 'COP'].includes(order?.paymentmethodUsed);
    const steps = [
      {
        label: 'Order received',
        active: Boolean(order),
      },
      {
        label: cashPayment ? `Pay on ${isDelivery ? 'delivery' : 'pickup'}` : 'Payment confirmed',
        active: cashPayment ? !['CANCELLED', 'FAILED'].includes(orderStatus) : Boolean(order?.paidAt) || ['PAID', 'PREPARING', 'READY', 'OUT_FOR_DELIVERY', 'COMPLETED'].includes(orderStatus),
      },
      {
        label: 'Vendor preparing',
        active: ['PREPARING', 'READY', 'OUT_FOR_DELIVERY', 'COMPLETED'].includes(orderStatus),
      },
      {
        label: isDelivery ? 'Out for delivery' : 'Ready for pickup',
        active: isDelivery ? orderStatus === 'OUT_FOR_DELIVERY' || ['ORDER_PICKED_UP', 'DELIVERED'].includes(deliveryStatus) : ['READY', 'COMPLETED'].includes(orderStatus),
      },
      {
        label: isDelivery ? 'Delivered' : 'Picked up',
        active: orderStatus === 'COMPLETED' || (isDelivery && deliveryStatus === 'DELIVERED'),
      },
    ];

    return (
      <div className="space-y-4 mt-4">
        {steps.map((step, index) => (
          <div key={step.label} className="flex items-start gap-4">
            <div className="mt-1">
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  step.active ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {index + 1}
              </span>
            </div>
            <div>
              <p className={`font-semibold ${step.active ? 'text-gray-900' : 'text-gray-500'}`}>
                {step.label}
              </p>
              {index === 0 && order && (
                <p className="text-sm text-gray-500">Placed on {new Date(order.createdAt).toLocaleString()}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const cancellationRequested = Boolean(order?.cancellationRequest?.requestedAt);
  const cancellableStatuses = ['PENDING_PAYMENT', 'PENDING', 'PAID'];
  const cancellationRequestStatuses = ['PREPARING', 'READY', 'OUT_FOR_DELIVERY'];
  const canCancel = cancellableStatuses.includes(order?.status) && !cancellationRequested;
  const canRequestCancellation = cancellationRequestStatuses.includes(order?.status) && !cancellationRequested;
  const cancelOrder = async () => {
    if (cancellationBusy) return;
    const request = canRequestCancellation;
    const confirmation = request
      ? 'This order is already in progress. Send a cancellation request for review?'
      : 'Cancel this order? This cannot be undone.';
    if (!window.confirm(confirmation)) return;
    setCancellationBusy(true);
    setError('');
    setCancellationMessage('');
    try {
      const response = await post(`/orders/${id}/cancellation`, {});
      setOrder(response.order);
      setDelivery(response.delivery || null);
      setCancellationMessage(response.message);
    } catch (cancelError) {
      setError(cancelError.response?.data?.message || 'Unable to cancel this order.');
    } finally {
      setCancellationBusy(false);
    }
  };

  const submitRating = async (event) => {
    event.preventDefault();
    if (ratingBusy) return;
    setRatingBusy(true);
    setRatingError('');
    setRatingMessage('');
    try {
      const response = await post(`/orders/${id}/rating`, {
        score: ratingScore,
        comment: ratingComment,
      });
      if (!response?.success || !response.order?.rating?.score) {
        throw new Error(response?.message || 'The store rating was not saved.');
      }
      setOrder((current) => ({
        ...current,
        ...response.order,
        vendorId: current.vendorId,
      }));
      setRatingMessage('Thank you for rating this store.');
    } catch (ratingSubmitError) {
      setRatingError(
        ratingSubmitError.response?.data?.message ||
          ratingSubmitError.message ||
          'Unable to submit your rating.',
      );
    } finally {
      setRatingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 py-12 px-4">
        <OrderHeading />
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <OrderHeading />
        <div className="text-center text-red-600 font-semibold">{error}</div>
      </div>
    );
  }

  if (!order) {
    return <div className="max-w-4xl mx-auto space-y-6"><OrderHeading /><p className="text-center">Order details not available.</p></div>;
  }

  const displayedStatus = order.status === 'READY' && order.orderType !== 'DELIVERY'
    ? 'Ready for pickup'
    : statusLabel[order.status] || order.status;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <OrderHeading order={order} status={displayedStatus} />

      {cancellationMessage && <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">{cancellationMessage}</p>}
      {cancellationRequested && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Cancellation requested. The vendor and support team have been notified.</p>}
      {order.status === 'COMPLETED' && order.orderChannel !== 'POS' && !order.rating?.score && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle>Rate your store experience</CardTitle>
            <CardDescription>Please rate your completed order with {order.vendorId?.storeName || 'this store'}.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitRating} className="space-y-4">
              <p className="text-sm text-gray-600">A rating is required for every completed customer order.</p>
              <div className="flex items-center gap-2" role="group" aria-label="Store rating">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    type="button"
                    aria-label={`${score} star${score === 1 ? '' : 's'}`}
                    aria-pressed={ratingScore === score}
                    onClick={() => setRatingScore(score)}
                    className="rounded p-1 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <Star className={`h-7 w-7 ${score <= ratingScore ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Comment (optional)
                <textarea
                  maxLength={1000}
                  value={ratingComment}
                  onChange={(event) => setRatingComment(event.target.value)}
                  className="mt-1 min-h-24 w-full rounded-lg border p-3"
                />
              </label>
              {ratingError && <p role="alert" className="text-sm text-red-700">{ratingError}</p>}
              <Button type="submit" disabled={ratingBusy || ratingScore === 0}>
                {ratingBusy ? 'Submitting rating...' : 'Submit rating'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
      {ratingMessage && <p role="status" className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">{ratingMessage}</p>}
      {order.rating?.score && (
        <p role="status" className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
          Your rating: <span className="font-semibold">{order.rating.score}/5 stars</span>
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1.8fr_1.2fr]">
        <div className="space-y-6">
          <Card className="border">
            <CardHeader>
              <CardTitle>Order progress</CardTitle>
              <CardDescription>{order.orderType === 'DELIVERY' ? 'Follow your order from preparation to delivery.' : 'Follow your order from preparation to pickup.'}</CardDescription>
            </CardHeader>
            <CardContent>{renderTimeline()}</CardContent>
          </Card>
          <Card className="border">
            <CardHeader>
              <CardTitle>{order.orderType === 'DELIVERY' ? 'Store & Delivery' : 'Store & Pickup'}</CardTitle>
              <CardDescription>{order.orderType === 'DELIVERY' ? 'Store information and delivery assignment.' : 'Store information for collecting your order.'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Store</p>
                  <p className="font-semibold text-gray-900">{order.vendorId?.storeName || 'Unknown vendor'}</p>
                  {order.vendorId?.completeAddress && (
                    <p className="text-sm text-gray-500">{order.vendorId.completeAddress}</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="font-semibold text-gray-900">{paymentLabel[order.paymentmethodUsed] || order.paymentmethodUsed}</p>
                </div>

                {order.orderType !== 'DELIVERY' ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">Collect your order at the store when it is ready for pickup.</div>
                ) : delivery ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm text-gray-500">Delivery status</p>
                    <p className="font-semibold text-gray-900">{delivery.status}</p>
                    {delivery.rider?.userId ? (
                      <div className="mt-3 text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-semibold text-gray-900">Rider:</span> {delivery.rider.userId.name || 'Assigned rider'}
                        </p>
                        {delivery.rider.userId.phone && (
                          <a href={`tel:${delivery.rider.userId.phone}`} className="inline-flex items-center gap-1 text-primary underline">Phone: {delivery.rider.userId.phone}</a>
                        )}
                        {delivery.pickupTime && (
                          <p>Picked up at: {new Date(delivery.pickupTime).toLocaleString()}</p>
                        )}
                        {delivery.dropoffTime && (
                          <p>Dropped off at: {new Date(delivery.dropoffTime).toLocaleString()}</p>
                        )}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-gray-600">A rider will be assigned once the delivery is accepted.</p>
                    )}
                    <div className="mt-4 rounded-lg bg-white p-3 text-sm text-gray-600">
                      <p className="font-semibold text-gray-800">Drop-off address</p>
                      <p>{delivery.dropoffAddress || 'Not specified'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                    Delivery job pending. We will assign a rider shortly.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border">
            <CardHeader>
              <CardTitle>Order items</CardTitle>
              <CardDescription>Review items and pricing breakdown.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-gray-200">
                <TotalWeight items={order.items} />
                {order.items?.map((item) => (
                  <div key={`${item.productId}-${item.name}`} className="flex justify-between py-4">
                    <div>
                      <div className="font-semibold text-gray-900">{item.name}</div>
                      <WeightInfo {...item} />
                      <div className="text-sm text-gray-500">Qty: {item.qty}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">₱{(item.price * item.qty).toFixed(2)}</div>
                      <div className="text-sm text-gray-500">₱{item.price.toFixed(2)} each</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border">
            <CardHeader>
              <CardTitle>Summary</CardTitle>
              <CardDescription>Pricing details for this order.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₱{order.pricing?.subtotal?.toFixed(2) ?? '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery fee</span>
                  <span>₱{order.pricing?.deliveryFee?.toFixed(2) ?? '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Commission</span>
                  <span>₱{order.pricing?.commission?.toFixed(2) ?? '0.00'}</span>
                </div>
                <div className="h-px bg-gray-200 my-3" />
                <div className="flex justify-between font-semibold text-gray-900">
                  <span>Total</span>
                  <span>₱{order.pricing?.total?.toFixed(2) ?? '0.00'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {(canCancel || canRequestCancellation) && <div className="flex justify-end">
            <Button variant="destructive" disabled={cancellationBusy} onClick={cancelOrder}>
              {cancellationBusy ? 'Submitting...' : canRequestCancellation ? 'Request Cancellation' : 'Cancel Order'}
            </Button>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;

function OrderHeading({ order, status }) {
  return <header className="flex items-start justify-between gap-3">
    <div className="flex min-w-0 items-start gap-2">
      <Link to="/orders" aria-label="Back to orders" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></Link>
      <div className="min-w-0 pt-1.5"><h1 className="text-lg font-semibold">Order details</h1>{order && <p className="mt-1 text-sm text-gray-500">#{orderReference(order._id)} · {new Date(order.createdAt).toLocaleString()}</p>}</div>
    </div>
    {status && <span className="mt-1.5 shrink-0 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">{status}</span>}
  </header>;
}
